import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { microBrand } from './brand';
import { easeOutQuiet, prefersReducedMotion } from './reducedMotion';
import './segmented-control.scss';
const DURATION_MS = 180;
const TRAIL_MS = 35;
/**
 * Radio-group segmented control with a black selection thumb, in the spirit of React Bits Rubber Segment
 * but built in CSS without the `motion` dependency. The thumb edges move separately; the trailing edge
 * lags by at most the brand's stretch (Rebels 8% of the thumb width, QPortal none). No overshoot.
 */
export function SegmentedControl({ options, value, onChange, ariaLabel, ariaLabelledBy, fullWidth = false, disabled = false, notifyOnReselect = false }) {
    const thumbRef = useRef(null);
    const trackRef = useRef(null);
    const optionRefs = useRef([]);
    const edges = useRef(null);
    const frame = useRef(0);
    const selectedIndex = options.findIndex((option) => option.value === value);
    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;
    const paint = (next) => {
        const thumb = thumbRef.current;
        if (!thumb)
            return;
        if (!next) {
            thumb.style.opacity = '0';
            return;
        }
        thumb.style.opacity = '1';
        thumb.style.transform = `translateX(${next.left}px)`;
        thumb.style.width = `${Math.max(0, next.right - next.left)}px`;
    };
    const measure = (index) => {
        const option = optionRefs.current[index];
        if (!option)
            return null;
        return { left: option.offsetLeft, right: option.offsetLeft + option.offsetWidth };
    };
    const moveTo = useCallback((index, animate) => {
        cancelAnimationFrame(frame.current);
        const target = index >= 0 ? measure(index) : null;
        const from = edges.current;
        if (!target || !from || !animate || prefersReducedMotion()) {
            edges.current = target;
            paint(target);
            return;
        }
        const movingRight = target.left > from.left;
        const distance = Math.max(Math.abs(target.left - from.left), Math.abs(target.right - from.right));
        const cap = microBrand.segmentStretch * (target.right - target.left);
        const lag = cap > 0 ? TRAIL_MS : 0;
        const start = performance.now();
        const step = (now) => {
            const elapsed = now - start;
            const lead = easeOutQuiet(Math.min(1, elapsed / DURATION_MS));
            let trail = lag ? easeOutQuiet(Math.min(1, Math.max(0, (elapsed - lag) / (DURATION_MS - lag)))) : lead;
            if (distance > 0 && lead - trail > cap / distance)
                trail = lead - cap / distance;
            const next = {
                left: from.left + (target.left - from.left) * (movingRight ? trail : lead),
                right: from.right + (target.right - from.right) * (movingRight ? lead : trail)
            };
            edges.current = next;
            paint(next);
            if (elapsed < DURATION_MS) {
                frame.current = requestAnimationFrame(step);
            }
            else {
                edges.current = target;
                paint(target);
            }
        };
        frame.current = requestAnimationFrame(step);
    }, []);
    useLayoutEffect(() => {
        moveTo(selectedIndex, true);
    }, [selectedIndex, options.length, moveTo]);
    useEffect(() => {
        const track = trackRef.current;
        if (!track || typeof ResizeObserver === 'undefined')
            return undefined;
        const observer = new ResizeObserver(() => moveTo(selectedIndexRef.current, false));
        observer.observe(track);
        return () => observer.disconnect();
    }, [moveTo]);
    useEffect(() => () => cancelAnimationFrame(frame.current), []);
    const select = (index, event, focus) => {
        const option = options[index];
        if (!option || disabled)
            return;
        if (focus)
            optionRefs.current[index]?.focus();
        if (option.value !== value || notifyOnReselect)
            onChange(option.value, event);
    };
    const handleKeyDown = (event, index) => {
        const last = options.length - 1;
        let next = null;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                next = index >= last ? 0 : index + 1;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                next = index <= 0 ? last : index - 1;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = last;
                break;
            default:
                return;
        }
        event.preventDefault();
        select(next, event, true);
    };
    const style = { '--sc-hover': microBrand.hover, '--sc-focus': microBrand.focus };
    return (_jsxs("div", { ref: trackRef, role: 'radiogroup', "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-disabled": disabled || undefined, className: `segmented-control${fullWidth ? ' segmented-control--full' : ''}`, style: style, children: [_jsx("span", { ref: thumbRef, className: 'segmented-control__thumb', "aria-hidden": 'true' }), options.map((option, index) => {
                const checked = index === selectedIndex;
                const focusable = selectedIndex === -1 ? index === 0 : checked;
                return (_jsx("button", { ref: (element) => {
                        optionRefs.current[index] = element;
                    }, type: 'button', role: 'radio', "aria-checked": checked, tabIndex: focusable ? 0 : -1, disabled: disabled, className: 'segmented-control__option', onClick: (event) => select(index, event, false), onKeyDown: (event) => handleKeyDown(event, index), children: option.label }, option.value));
            })] }));
}
